/**
 * Hover ground-effect: a soft ring of displaced air on the terrain under a
 * low, slow ship — the visual counterpart of the vectored thrusters that
 * hold it up.
 *
 * Not a flat decal: the ring is a polar vertex grid DRAPED onto the
 * analytic terrain every frame (same sampler the meshes are built from),
 * so it hugs rocks, slopes and shorelines instead of slicing through
 * them. Radial canvas texture, additive, pulsing with a slow spin;
 * opacity tracks how hard the thrusters work (low + slow = strong,
 * gone by ~14 m altitude).
 */
import * as THREE from "three";
import type { Planet } from "../world/planet";

const MAX_ALT = 14;
const MAX_SPEED = 30;
/** Polar grid: concentric vertex rings × segments. */
const RINGS = 5;
const SEGS = 28;
/** Faint per-segment radius jitter — almost circular, never mechanical. */
const LOBES = new Float32Array(SEGS);
for (let j = 0; j < SEGS; j++) {
  LOBES[j] = Math.sin(j * 2.39996) * 0.025 + Math.sin(j * 0.97 + 1.7) * 0.018;
}
/** Lift above the surface so the additive quad never z-fights terrain. */
const LIFT = 0.18;

function makeRingTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const c = size / 2;
  const ring = ctx.createRadialGradient(c, c, 0, c, c, c);
  ring.addColorStop(0.0, "rgba(160, 210, 255, 0.10)");
  ring.addColorStop(0.55, "rgba(170, 215, 255, 0.05)");
  ring.addColorStop(0.78, "rgba(190, 230, 255, 0.55)");
  ring.addColorStop(0.9, "rgba(170, 215, 255, 0.18)");
  ring.addColorStop(1.0, "rgba(160, 210, 255, 0)");
  ctx.fillStyle = ring;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

export class HoverRing {
  private mesh: THREE.Mesh;
  private mat: THREE.MeshBasicMaterial;
  private positions: THREE.BufferAttribute;
  private t = 0;
  private dir = new THREE.Vector3();
  private t1 = new THREE.Vector3();
  private t2 = new THREE.Vector3();
  private v = new THREE.Vector3();
  private _invQ = new THREE.Quaternion();

  constructor(scene: THREE.Scene) {
    // Polar grid: center vertex + RINGS×SEGS. UVs are build-time polar
    // coords, so the texture (and its streaks) rotates with the vertices.
    const count = 1 + RINGS * SEGS;
    const pos = new Float32Array(count * 3);
    const uv = new Float32Array(count * 2);
    uv[0] = 0.5;
    uv[1] = 0.5;
    for (let i = 1; i <= RINGS; i++) {
      const rr = i / RINGS;
      for (let j = 0; j < SEGS; j++) {
        const a = (j / SEGS) * Math.PI * 2;
        const k = 1 + (i - 1) * SEGS + j;
        uv[k * 2] = 0.5 + 0.5 * rr * Math.cos(a);
        uv[k * 2 + 1] = 0.5 + 0.5 * rr * Math.sin(a);
      }
    }
    const indices: number[] = [];
    for (let j = 0; j < SEGS; j++) {
      indices.push(0, 1 + j, 1 + ((j + 1) % SEGS)); // center fan
    }
    for (let i = 0; i < RINGS - 1; i++) {
      const a0 = 1 + i * SEGS;
      const b0 = a0 + SEGS;
      for (let j = 0; j < SEGS; j++) {
        const j1 = (j + 1) % SEGS;
        indices.push(a0 + j, b0 + j, b0 + j1, a0 + j, b0 + j1, a0 + j1);
      }
    }
    const geo = new THREE.BufferGeometry();
    this.positions = new THREE.BufferAttribute(pos, 3);
    this.positions.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("position", this.positions);
    geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
    geo.setIndex(indices);

    this.mat = new THREE.MeshBasicMaterial({
      map: makeRingTexture(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      opacity: 0,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.visible = false;
    this.mesh.frustumCulled = false; // verts are written in render space
    this.mesh.renderOrder = 4; // above terrain, below the atmosphere shell
    scene.add(this.mesh);
  }

  /** Current faded opacity (0 = hidden) — smoke tests read this. */
  get opacity(): number {
    return this.mat.opacity;
  }

  /**
   * Drive from the hovering ship's state; vertices are draped onto the
   * planet's analytic terrain around the point below `shipPos`.
   */
  update(
    dt: number,
    active: boolean,
    planet: Planet | null,
    shipPos: THREE.Vector3,
    origin: THREE.Vector3,
    altitude: number,
    speed: number,
  ): void {
    this.t += dt;
    const strength =
      active && planet
        ? Math.max(0, 1 - altitude / MAX_ALT) * Math.max(0, 1 - Math.abs(speed) / MAX_SPEED)
        : 0;
    this.mat.opacity += (strength * 0.3 - this.mat.opacity) * Math.min(1, 8 * dt);
    this.mesh.visible = this.mat.opacity > 0.02;
    if (!this.mesh.visible || !planet) return;

    const def = planet.def;
    const sea = def.seaRadius ?? -Infinity;
    // Drape in the SAME interpolated render frame the terrain mesh uses — planet.group
    // (lerped center + spin between sim ticks) — NOT the raw sim-tick pose. The bodies now
    // orbit/spin ~10× faster in world space, so the per-tick gap between the two grew to
    // metres and made the ring shear off the ground every frame (chaotic flicker). Working
    // in planet-LOCAL space and mapping back through group keeps the ring welded to the ground.
    const grp = planet.group;
    this._invQ.copy(grp.quaternion).invert();
    // Ship's planet-LOCAL ground direction (shipPos is the INTERPOLATED visual pose).
    this.dir.copy(shipPos).sub(origin).sub(grp.position).applyQuaternion(this._invQ).normalize();
    // Tangent basis around the ground point; spin rotates the sample fan.
    this.t1.set(1, 0, 0).cross(this.dir);
    if (this.t1.lengthSq() < 1e-6) this.t1.set(0, 0, 1).cross(this.dir);
    this.t1.normalize();
    this.t2.crossVectors(this.dir, this.t1);

    const pulse = 1 + Math.sin(this.t * 2.6) * 0.07;
    const radius = (7 + altitude * 0.45) * pulse;
    const spin = this.t * 0.4;
    const arr = this.positions.array as Float32Array;

    const drape = (k: number, offT1: number, offT2: number): void => {
      this.v
        .copy(this.dir)
        .addScaledVector(this.t1, offT1 / def.radius)
        .addScaledVector(this.t2, offT2 / def.radius)
        .normalize();
      // Local-space surface radius, then map local → render via the SAME group transform
      // the terrain renders through (grp.quaternion * local + grp.position).
      const r = Math.max(planet.sampler.surfaceRadius(this.v.x, this.v.y, this.v.z), sea) + LIFT;
      this.v.multiplyScalar(r).applyQuaternion(grp.quaternion).add(grp.position);
      arr[k * 3] = this.v.x;
      arr[k * 3 + 1] = this.v.y;
      arr[k * 3 + 2] = this.v.z;
    };

    drape(0, 0, 0);
    for (let i = 1; i <= RINGS; i++) {
      for (let j = 0; j < SEGS; j++) {
        const a = (j / SEGS) * Math.PI * 2 + spin;
        // Lobed, slowly-roiling edge: static per-angle jitter plus a
        // traveling wave, both growing toward the rim.
        const wobble =
          1 + (LOBES[j] + Math.sin(this.t * 1.9 + j * 1.31) * 0.02) * (i / RINGS);
        const rr = ((radius * i) / RINGS) * wobble;
        drape(1 + (i - 1) * SEGS + j, Math.cos(a) * rr, Math.sin(a) * rr);
      }
    }
    this.positions.needsUpdate = true;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mat.map?.dispose();
    this.mat.dispose();
  }
}
